import type { BusinessType } from "@/context/TenantContext";

type Replacement = readonly [RegExp, string];

// Per-business-type noun swaps applied to `t()` output via `tb()`.
// The regexes use \b word boundaries so only whole-word matches are replaced.
// Case is preserved: replacements match the casing of the source word.
const SUBSTITUTIONS: Record<BusinessType, readonly Replacement[]> = {
  PARKING: [],
  RAILWAY_CLOAK_ROOM: [
    [/\bparking\b/gi, "cloak room"],
    [/\bvehicle\b/gi, "item"],
    [/\bvehicles\b/gi, "items"],
  ],
  ROAD_TAX: [
    [/\bparking\b/gi, "road tax"],
  ],
};

function preserveCase(source: string, replacement: string): string {
  if (!source) return replacement;
  // All upper, e.g. PARKING -> CLOAK ROOM
  if (source === source.toUpperCase() && source.toLowerCase() !== source) {
    return replacement.toUpperCase();
  }
  // Leading capital, e.g. Parking -> Cloak room -> Cloak Room (title-case the whole thing)
  if (source[0] === source[0].toUpperCase() && source[0] !== source[0].toLowerCase()) {
    return replacement
      .split(" ")
      .map((w) => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : w))
      .join(" ");
  }
  return replacement;
}

export function applyBusinessLabels(text: string, businessType: BusinessType): string {
  const rules = SUBSTITUTIONS[businessType];
  if (!rules || rules.length === 0) return text;
  let out = text;
  for (const [pattern, replacement] of rules) {
    out = out.replace(pattern, (match) => preserveCase(match, replacement));
  }
  return out;
}

// Maps each businessType to the namespace segment used in locale keys.
// Locale entries live under `business.<segment>.<semanticKey>` in en/hindi/marathi.
const LOCALE_NAMESPACE: Record<BusinessType, string> = {
  PARKING: "parking",
  RAILWAY_CLOAK_ROOM: "cloakRoom",
  ROAD_TAX: "roadTax",
};

// Returns the locale key for a business-specific UI string.
// Render via `t(bizKey(businessType, "createOrgTitle"))` — never hard-code
// per-business strings in JSX. Add new semantic keys to all three locale files.
export function bizKey(businessType: BusinessType, semanticKey: string): string {
  return `business.${LOCALE_NAMESPACE[businessType]}.${semanticKey}`;
}

export const BUSINESS_TYPES: readonly BusinessType[] = ["PARKING", "RAILWAY_CLOAK_ROOM", "ROAD_TAX"] as const;

// Fixed UI field labels per business type. Used by tenant-creation form,
// settings page, records column headers — anywhere a static field name
// deserves distinct terminology rather than a regex-driven swap.
export const FIELD_LABELS: Record<
  BusinessType,
  {
    totalAvailableSpace: string;
    entityNoun: string;       // singular: "Vehicle" / "Item" / "Vehicle"
    entityNounPlural: string;
    customerIdentifier: string; // "Vehicle Number" / "Mobile Number" / "Vehicle Number"
    businessTypeLabel: string;
  }
> = {
  PARKING: {
    totalAvailableSpace: "Total Parking Space",
    entityNoun: "Vehicle",
    entityNounPlural: "Vehicles",
    customerIdentifier: "Vehicle Number",
    businessTypeLabel: "Parking",
  },
  RAILWAY_CLOAK_ROOM: {
    totalAvailableSpace: "Total Item Capacity",
    entityNoun: "Item",
    entityNounPlural: "Items",
    customerIdentifier: "Mobile Number",
    businessTypeLabel: "Railway Cloak Room",
  },
  ROAD_TAX: {
    totalAvailableSpace: "Total Taxable Vehicle Capacity",
    entityNoun: "Vehicle",
    entityNounPlural: "Vehicles",
    customerIdentifier: "Vehicle Number",
    businessTypeLabel: "Road Tax",
  },
};
